import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { X } from 'lucide-react';

import apiClient from '../../services/apiClient';

export function ProjectEditModal({
  open,
  projectId,
  initialTitle,
  initialObjective,
  onClose,
  onSaved,
}: {
  open: boolean;
  projectId: string;
  initialTitle: string;
  initialObjective: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(initialTitle || '');
  const [objective, setObjective] = useState(initialObjective || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle || '');
    setObjective(initialObjective || '');
  }, [open, initialTitle, initialObjective]);

  const canSave = useMemo(() => {
    return Boolean(title.trim()) && !saving;
  }, [title, saving]);

  async function save() {
    if (!canSave) return;
    setSaving(true);
    try {
      await apiClient.patch(`/projects/${encodeURIComponent(projectId)}`, {
        title: title.trim(),
        description: objective.trim(),
      });
      onSaved();
      try {
        window.dispatchEvent(new CustomEvent('project.updated'));
        window.dispatchEvent(new CustomEvent('project.tasks.refresh'));
      } catch {}
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop, not on the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    // Stop all clicks inside the modal from bubbling to parent elements
    e.stopPropagation();
  };

  const handleButtonClick = (e: React.MouseEvent, handler: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    handler();
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl my-auto rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-950 flex flex-col max-h-[90vh]"
        onClick={handleModalClick}
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-neutral-800 flex-shrink-0">
          <div>
            <div className="text-base font-semibold text-slate-900 dark:text-white">Edit Project</div>
            <div className="mt-1 text-xs text-slate-500 dark:text-neutral-400">
              Update the project name and objective.
            </div>
          </div>
          <button
            className="rounded-lg p-2 hover:bg-slate-100 dark:hover:bg-neutral-900"
            onClick={(e) => handleButtonClick(e, onClose)}
            aria-label="Close"
          >
            <X className="h-5 w-5 text-slate-600 dark:text-neutral-300" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-4 p-5">
            <div>
              <div className="text-xs font-semibold text-slate-700 dark:text-neutral-300">Name</div>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Project name…"
                disabled={saving}
              />
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-700 dark:text-neutral-300">Objective</div>
              <textarea
                className="mt-1 min-h-[110px] w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Objective / description…"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4 dark:border-neutral-800 flex-shrink-0">
          <button
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:bg-neutral-900"
            onClick={(e) => handleButtonClick(e, onClose)}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            onClick={(e) => handleButtonClick(e, () => void save())}
            disabled={!canSave}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal via portal to body, completely outside the card's DOM hierarchy
  return createPortal(modalContent, document.body);
}

